// antd v5 officially supports React 16-18; this project is on React 19, which
// triggers "[antd: compatible] antd v5 support React is 16 ~ 18" and breaks the
// static message/notification/Modal APIs (they still use the removed
// ReactDOM.render). This is Ant Design's own compatibility shim - it must be
// imported before antd is used.
import '@ant-design/v5-patch-for-react-19'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux';
import store from './redux/store';
import 'antd/dist/reset.css';
import { initI18n } from './i18n';
import { installNotifyBridge } from './utils/notifyBridge';

// One toast channel: antd message/notification calls render in the app's single Toaster.
installNotifyBridge();
import { bootLocale } from './i18n/orgLocaleSettings';
// LocaleProvider owns the antd ConfigProvider: direction, locale and the Arabic font token have to
// change together with the brand theme, so they live in one provider rather than two.
// See src/i18n/LocaleProvider.tsx and docs/43-I18N-ARABIC-DESIGN.md.
import LocaleProvider from './i18n/LocaleProvider';
import { importWithRetry, isChunkLoadError } from './utils/lazyWithRetry';
import { t } from './i18n/t';
import { applyUiVersion } from './theme/uiFlags';
import { initTheme } from './theme/themeMode';

// Resolved before anything renders, so no screen paints with one sizing system and then the other.
applyUiVersion();
initTheme();

/**
 * i18next is initialised BEFORE the app is imported and rendered. This is load-bearing.
 *
 * Much of this app calls `t()` from **module scope** — antd column definitions, status maps, and
 * the sidebar's `MODULE_META`. Those are evaluated once, at import time. If i18next is not ready by
 * then they resolve to the English fallback and stay English for the whole session, no matter what
 * the provider does afterwards: a remount re-runs render, but it cannot re-evaluate a module-scope
 * constant. That is exactly how the sidebar's group headings (Home/People/Payroll/…) stayed English
 * in an otherwise fully Arabic app.
 *
 * So: resolve the language, load the catalogues, and only then pull in `App` via a dynamic import
 * so its module graph — and every module-scope `t()` in it — evaluates against the right language.
 */
const boot = async () => {
  const locale = bootLocale(navigator.language);

  // A catalogue that will not load must never stop the app booting. `t()` falls back to the
  // eagerly-bundled English catalogue (see src/i18n/t.ts), so an English UI is a far better
  // outcome than a permanently blank page.
  try {
    await initI18n(locale);
  } catch (error) {
    if (import.meta.env.DEV) console.error('[boot] i18n init failed, continuing in English', error);
  }

  // This dynamic import pulls in the entire application graph. Before the retry wrapper, a single
  // failed chunk fetch here rejected `boot()`, `createRoot(...).render()` was never reached and the
  // page stayed blank forever with nothing on screen to recover from — one half of the reported
  // "blank screen, only a refresh fixes it".
  const { default: App } = await importWithRetry(() => import('./App'));

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Provider store={store}>
        <LocaleProvider initialLocale={locale}>
          <App />
        </LocaleProvider>
      </Provider>
    </StrictMode>,
  );
};

/**
 * Last-resort recovery UI, painted straight into `#root` with plain DOM.
 *
 * Reached only when boot itself failed, which means no React tree exists and no component chunk
 * can be trusted to load — so this deliberately uses no framework, no antd and no lazy import.
 */
const renderBootFailure = () => {
  const root = document.getElementById('root');
  if (!root || root.childElementCount > 0) return;

  // The stylesheet may be exactly what failed to load, so the colours are resolved here from the
  // mode `initTheme` already put on <html>, not from CSS variables.
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const ink = dark ? '#E8EAF2' : '#111827';
  const muted = dark ? '#A9AFC2' : '#374151';
  const fill = dark ? '#5458E8' : '#4F46E5';

  const wrap = document.createElement('div');
  wrap.setAttribute(
    'style',
    'display:flex;flex-direction:column;align-items:center;justify-content:center;'
      + `gap:14px;min-height:100vh;font-family:Poppins,system-ui,sans-serif;color:${muted};`
      + `background:${dark ? '#14161F' : '#FFFFFF'};text-align:center;padding:24px;`,
  );

  const heading = document.createElement('div');
  heading.setAttribute('style', `font-size:16px;font-weight:600;color:${ink};`);
  heading.textContent = t('shell:errorboundary.somethingWentWrong');

  const detail = document.createElement('div');
  detail.setAttribute('style', 'font-size:13px;max-width:340px;');
  detail.textContent = t('errors:network.timeout.description');

  const button = document.createElement('button');
  button.type = 'button';
  button.setAttribute(
    'style',
    `border:1px solid ${fill};border-radius:8px;background:${fill};color:#fff;`
      + 'font-size:13px;padding:7px 18px;cursor:pointer;',
  );
  button.textContent = t('common:actions.refresh');
  button.addEventListener('click', () => window.location.reload());

  wrap.append(heading, detail, button);
  root.append(wrap);
};

/**
 * Vite fires this when a `<link rel="modulepreload">` for a chunk fails — the same stale-deploy
 * situation `lazyWithRetry` handles, but caught earlier and for chunks nothing has awaited yet.
 * Preventing the default stops Vite throwing an unhandled error; `lazyWithRetry` still owns the
 * decision about whether to reload, so a broken deploy cannot loop here either.
 */
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  if (import.meta.env.DEV) console.warn('[boot] chunk preload failed', event);
});

void boot().catch((error) => {
  if (import.meta.env.DEV) console.error('[boot] failed', error, isChunkLoadError(error));
  renderBootFailure();
});
